/* eslint-disable max-classes-per-file */
import { ErrorCode } from '@ps/web-core-enums';
import { DebugKey, canUseDOM, createDebug } from '@ps/web-core-utils';
import * as Sentry from '@sentry/react';
import { invert, snakeCase } from 'lodash';

import { StatusCode } from './enums/StatusCode';
import type { HttpHeaders, ResponseHeaders, StompHeaders } from './types';

const statusKeys = invert(StatusCode);

export interface ErrorBody {
  code: string;
  message?: string;
  text: string;
}

const isErrorBody = (content: unknown): content is ErrorBody => !!(content as ErrorBody)?.code;

export class RequestError<TBody = ErrorBody> extends Error {
  headers?: StompHeaders | HttpHeaders | ResponseHeaders;
  body: ErrorBody;
  statusCode = 500;
  private response?: Response;
  private service: string;
  private requestId?: string;

  constructor({
    service,
    body,
    response,
    method,
    headers,
    requestId,
    cause
  }: {
    service: string;
    body: TBody;
    response?: Response;
    method: string;
    headers?: StompHeaders;
    requestId?: string;
    cause?: ErrorOptions['cause'];
  }) {
    const statusCode = response?.status;
    const name = statusCode && statusCode >= 400 ? statusCode : method;
    let innerBody;

    if (isErrorBody(body)) {
      innerBody = { ...body };
    } else if (headers?.['status-code']) {
      const { ['status-code']: errorCode } = headers;
      const code = statusKeys[errorCode] || snakeCase(errorCode).toUpperCase();
      innerBody = { code, text: '' };
    } else {
      innerBody = { code: ErrorCode.UNKNOWN_ERROR, text: '' };
    }

    super(`${name} ${service} ${innerBody.code}`, { cause });

    // https://github.com/Microsoft/TypeScript/wiki/FAQ#why-doesnt-extending-built-ins-like-error-array-and-map-work
    Object.setPrototypeOf(this, RequestError.prototype);

    Sentry.addBreadcrumb({
      type: 'debug',
      category: 'request-error',
      message: this.message,
      data: { method, service, statusCode: response?.status }
    });

    this.name = 'RequestError';
    this.body = innerBody;
    this.response = response;
    this.service = service;
    this.requestId = requestId;
    this.headers = headers || (response?.headers as HttpHeaders);
    this.statusCode = statusCode || 500;
  }

  log() {
    const debug = createDebug(DebugKey.Request, this.requestId);
    debug('failed %s %s', this.service, this.message.replace(` ${this.service}`, ''));
    if (process.env.NODE_ENV !== 'test' && (!this.response || this.response.ok) && canUseDOM) {
      // eslint-disable-next-line no-console
      console.error(this.message);
    }
  }
}
